import type { Signal } from '@preact/signals-react';
import { useSignals } from '@preact/signals-react/runtime';
import { Button } from '@workspace/ui/components/button';
import { Textarea } from '@workspace/ui/components/textarea';
import { Paperclip, SendHorizonal } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { AttachedFile, MessageStatus } from '~/hooks/use-chat/types';
import { useMentions } from '~/hooks/use-mentions';
import { useTeamMentions } from '~/hooks/use-team-mentions';
import { m } from '~/paraglide/messages.js';
import { useMembers } from '~/routes/_authenticated/persons/-hooks/use-members';
import { useCurrentOrganization } from '~/routes/_authenticated/persons/-hooks/use-organization';
import type { Team } from '~/types/team';
import { MentionDropdown } from './mention-dropdown';
import { TeamMentionDropdown } from './team-mention-dropdown';

export type ChatInputProps = {
  isDragging: boolean;
  isLoading: Signal<boolean>;
  input: Signal<string>;
  handleInputChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  attachedFiles: AttachedFile[];
  removeFile: (id: string) => void;
  handleFileSelect: (files: FileList | File[]) => void;
  handleSubmit: () => Promise<void>;
  currentMessageHasToolCall?: boolean;
  status: Signal<MessageStatus>;
};

export const ChatInput = ({
  isDragging,
  isLoading,
  input,
  handleInputChange,
  attachedFiles,
  removeFile,
  handleFileSelect,
  handleSubmit,
  currentMessageHasToolCall = false,
  status,
}: ChatInputProps) => {
  useSignals();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const teamDropdownRef = useRef<HTMLDivElement>(null);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [selectedTeamIndex, setSelectedTeamIndex] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [teamDropdownPosition, setTeamDropdownPosition] = useState({
    top: 0,
    left: 0,
  });

  const { orgUrn } = useCurrentOrganization();
  const { data: membersData } = useMembers(orgUrn, { page: 1, limit: 50 });

  // Mock users for testing
  const mockMembers: any[] = [
    {
      id: 'mock-1',
      name: 'Ana García',
      description: 'Desarrolladora Frontend',
      type: 'Empleado',
      relation: 'Contrato',
      amount: 75000,
      email: 'ana.garcia@empresa.com',
      phone: '+34 123 456 789',
      status: 'active',
    },
    {
      id: 'mock-2',
      name: 'Carlos Rodríguez',
      description: 'Diseñador UX/UI',
      type: 'Empleado',
      relation: 'Contrato',
      amount: 65000,
      email: 'carlos.rodriguez@empresa.com',
      phone: '+34 987 654 321',
      status: 'active',
    },
    {
      id: 'mock-3',
      name: 'María López',
      description: 'Gerente de Proyecto',
      type: 'Empleado',
      relation: 'Membresía',
      amount: 85000,
      email: 'maria.lopez@empresa.com',
      phone: '+34 555 123 456',
      status: 'active',
    },
    {
      id: 'mock-4',
      name: 'David Chen',
      description: 'Consultor Externo',
      type: 'Servicio',
      relation: 'Vendor',
      amount: 120000,
      email: 'david.chen@consultoria.com',
      phone: '+34 777 888 999',
      status: 'active',
    },
    {
      id: 'mock-5',
      name: 'Sofia Martínez',
      description: 'Especialista en Marketing',
      type: 'Proveedor',
      relation: 'Vendor',
      amount: 45000,
      email: 'sofia.martinez@marketing.com',
      phone: '+34 333 222 111',
      status: 'active',
    },
  ];

  // @ts-expect-error
  const apiMembers = membersData?.data?.members || [];
  const members = apiMembers.length > 0 ? apiMembers : mockMembers;

  // Mock teams for testing
  const mockTeams: Team[] = [
    {
      id: 'team-1',
      name: 'Frontend',
      description: 'Equipo de desarrollo frontend',
      type: 'Development',
      memberCount: 5,
      color: '#3b82f6',
      status: 'active',
    },
    {
      id: 'team-2',
      name: 'Backend',
      description: 'Equipo de desarrollo backend',
      type: 'Development',
      memberCount: 4,
      color: '#ef4444',
      status: 'active',
    },
    {
      id: 'team-3',
      name: 'Marketing',
      description: 'Equipo de marketing y comunicaciones',
      type: 'Marketing',
      memberCount: 3,
      color: '#f59e0b',
      status: 'active',
    },
    {
      id: 'team-4',
      name: 'Support',
      description: 'Equipo de atención al cliente',
      type: 'Support',
      memberCount: 6,
      color: '#10b981',
      status: 'active',
    },
    {
      id: 'team-5',
      name: 'Sales',
      description: 'Equipo de ventas',
      type: 'Sales',
      memberCount: 4,
      color: '#8b5cf6',
      status: 'active',
    },
  ];

  const teams = mockTeams;

  const {
    mentionState,
    filteredMembers,
    handleInputChange: handleMentionInputChange,
    handleMentionSelect: onMentionSelect,
    closeMentions,
  } = useMentions({
    onMentionSelect: (member: any) => {
      const currentValue = input.value;
      const beforeMention = currentValue.substring(
        0,
        mentionState.triggerPosition,
      );
      const afterCursor = currentValue.substring(mentionState.position);
      const newValue = `${beforeMention}@${member.name} ${afterCursor}`;

      input.value = newValue;

      // Focus back to textarea and set cursor position
      setTimeout(() => {
        if (textareaRef.current) {
          const newCursorPos = beforeMention.length + member.name.length + 2;
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    },
    members,
  });

  const {
    mentionState: teamMentionState,
    filteredTeams,
    handleInputChange: handleTeamMentionInputChange,
    handleTeamMentionSelect: onTeamMentionSelect,
    closeTeamMentions,
  } = useTeamMentions({
    onTeamMentionSelect: (team: Team) => {
      const currentValue = input.value;
      const beforeMention = currentValue.substring(
        0,
        teamMentionState.triggerPosition,
      );
      const afterCursor = currentValue.substring(teamMentionState.position);
      const newValue = `${beforeMention}#${team.name} ${afterCursor}`;

      input.value = newValue;

      // Focus back to textarea and set cursor position
      setTimeout(() => {
        if (textareaRef.current) {
          const newCursorPos = beforeMention.length + team.name.length + 2;
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    },
    teams,
  });

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const files: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }

      if (files.length > 0) {
        handleFileSelect(files);
      }
    },
    [handleFileSelect],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (mentionState.isOpen && filteredMembers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIndex((prev) => {
          const newIndex = prev < filteredMembers.length - 1 ? prev + 1 : 0;
          // Scroll to selected item
          setTimeout(() => {
            const selectedElement = dropdownRef.current?.querySelector(
              `[data-index="${newIndex}"]`,
            );
            selectedElement?.scrollIntoView({
              block: 'nearest',
              behavior: 'smooth',
            });
          }, 0);
          return newIndex;
        });
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIndex((prev) => {
          const newIndex = prev > 0 ? prev - 1 : filteredMembers.length - 1;
          // Scroll to selected item
          setTimeout(() => {
            const selectedElement = dropdownRef.current?.querySelector(
              `[data-index="${newIndex}"]`,
            );
            selectedElement?.scrollIntoView({
              block: 'nearest',
              behavior: 'smooth',
            });
          }, 0);
          return newIndex;
        });
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onMentionSelect(filteredMembers[selectedMentionIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMentions();
        return;
      }
    }

    if (teamMentionState.isOpen && filteredTeams.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedTeamIndex((prev) => {
          const newIndex = prev < filteredTeams.length - 1 ? prev + 1 : 0;
          // Scroll to selected item
          setTimeout(() => {
            const selectedElement = teamDropdownRef.current?.querySelector(
              `[data-index="${newIndex}"]`,
            );
            selectedElement?.scrollIntoView({
              block: 'nearest',
              behavior: 'smooth',
            });
          }, 0);
          return newIndex;
        });
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedTeamIndex((prev) => {
          const newIndex = prev > 0 ? prev - 1 : filteredTeams.length - 1;
          // Scroll to selected item
          setTimeout(() => {
            const selectedElement = teamDropdownRef.current?.querySelector(
              `[data-index="${newIndex}"]`,
            );
            selectedElement?.scrollIntoView({
              block: 'nearest',
              behavior: 'smooth',
            });
          }, 0);
          return newIndex;
        });
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onTeamMentionSelect(filteredTeams[selectedTeamIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        closeTeamMentions();
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleInputChangeWithMentions = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const value = event.currentTarget.value;
    const selectionStart = event.currentTarget.selectionStart || 0;

    handleInputChange(event);
    handleMentionInputChange(value, selectionStart);
    handleTeamMentionInputChange(value, selectionStart);
  };

  // Calculate dropdown position
  useEffect(() => {
    if (mentionState.isOpen && textareaRef.current) {
      const rect = textareaRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.top - 8, // Position above the textarea, anchored to bottom
        left: rect.left + 12, // Add some left padding
      });
      setSelectedMentionIndex(0); // Reset selection when dropdown opens
    }
  }, [mentionState.isOpen]);

  // Calculate team dropdown position
  useEffect(() => {
    if (teamMentionState.isOpen && textareaRef.current) {
      const rect = textareaRef.current.getBoundingClientRect();
      setTeamDropdownPosition({
        top: rect.top - 8, // Position above the textarea, anchored to bottom
        left: rect.left + 12, // Add some left padding
      });
      setSelectedTeamIndex(0); // Reset selection when dropdown opens
    }
  }, [teamMentionState.isOpen]);

  // Reset selected index when filtered results change
  // biome-ignore lint/correctness/useExhaustiveDependencies: only need to reset index when filtered results change
  useEffect(() => {
    setSelectedMentionIndex(0);
  }, [filteredMembers.length]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: only need to reset index when filtered team results change
  useEffect(() => {
    setSelectedTeamIndex(0);
  }, [filteredTeams.length]);

  // Close mentions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mentionState.isOpen &&
        textareaRef.current &&
        dropdownRef.current &&
        !textareaRef.current.contains(event.target as Node) &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        closeMentions();
      }

      if (
        teamMentionState.isOpen &&
        textareaRef.current &&
        teamDropdownRef.current &&
        !textareaRef.current.contains(event.target as Node) &&
        !teamDropdownRef.current.contains(event.target as Node)
      ) {
        closeTeamMentions();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [
    mentionState.isOpen,
    teamMentionState.isOpen,
    closeMentions,
    closeTeamMentions,
  ]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      className="bg-primary-foreground w-full border border-gray-800 rounded-lg overflow-hidden"
    >
      <div className="relative">
        {/* Animated gradient loader */}
        <div className="h-0.5 w-full bg-gray-800 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#2A9D90] to-transparent animate-pulse">
            <div className="h-full w-1/3 bg-gradient-to-r from-[#266209] via-[#2A9D90] to-[#266209] animate-[slide_2s_ease-in-out_infinite]" />
          </div>
        </div>

        {attachedFiles.length > 0 && (
          <div className="px-3 py-2">
            <div className="flex flex-wrap gap-2">
              {attachedFiles.map((attachedFile) => (
                <div
                  key={attachedFile.id}
                  className="flex items-center gap-2 bg-gray-800 rounded px-2 py-1 text-sm text-gray-300"
                >
                  {attachedFile.preview && (
                    <img
                      src={attachedFile.preview || '/placeholder.svg'}
                      alt="Preview"
                      className="w-6 h-6 object-cover rounded"
                    />
                  )}
                  <span className="truncate max-w-32">
                    {attachedFile.file.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeFile(attachedFile.id)}
                    className="text-gray-500 hover:text-red-400 ml-1"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="relative">
          <Textarea
            ref={textareaRef}
            value={input.value}
            onChange={handleInputChangeWithMentions}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={m['chat.input_placeholder']()}
            // placeholder={m['chat.input_placeholder_advanced']()}
            className="resize-none overflow-y-auto max-h-40 min-h-20 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 p-3 dark:bg-primary-foreground"
            disabled={currentMessageHasToolCall || status.value === 'submitted'}
          />
        </div>

        <div className="flex items-center gap-2 mt-2">
          <div className="flex items-center gap-2 m-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files) {
                  handleFileSelect(e.target.files);
                }
              }}
            />
            {/* <Button
              variant="default"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              className="bg-background text-primary hover:text-primary hover:bg-background-800"
            >
              <Paperclip className="h-4 w-4 mr-2" />
              Adjuntar Archivo
            </Button> */}
            <span className="font-medium text-muted-foreground text-sm">
              {input.value.length}/2000
            </span>
          </div>
        </div>

        {/* <div className="absolute top-3 right-3">
          <Select defaultValue="public">
            <SelectTrigger className="h-8 w-28 border-0 text-primary text-xs font-medium">
              <SelectValue placeholder={m['chat.select_public']()} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="public">
                <div className="flex items-center gap-2 font-medium text-xs">
                  <Users className="text-primary h-4 w-4" />
                  {m['chat.select_public']()}
                </div>
              </SelectItem>
              <SelectItem value="private">
                <div className="flex items-center gap-2 font-medium text-xs">
                  <Lock className="text-primary h-4 w-4" />
                  Private
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div> */}
        <div className="absolute bottom-3 right-3">
          <Button
            type="submit"
            size="icon"
            className="h-8 w-8 rounded-full"
            disabled={
              (!input.value.trim() && !attachedFiles.length) || isLoading.value
            }
            variant="ghost"
          >
            <SendHorizonal className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {isDragging && (
        <div className="absolute inset-0 bg-blue-500/10 border-2 border-dashed border-blue-500 rounded-lg flex items-center justify-center">
          <div className="text-blue-400 text-center">
            <Paperclip className="h-8 w-8 mx-auto mb-2" />
            <p>{m['chat.drop_files']()}</p>
          </div>
        </div>
      )}

      {/* Render mention dropdown using portal */}
      {mentionState.isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed w-72 z-[9999]"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              transform: 'translateY(-100%)',
            }}
          >
            <MentionDropdown
              members={filteredMembers}
              onSelect={onMentionSelect}
              selectedIndex={selectedMentionIndex}
            />
          </div>,
          document.body,
        )}

      {/* Render team mention dropdown using portal */}
      {teamMentionState.isOpen &&
        createPortal(
          <div
            ref={teamDropdownRef}
            className="fixed w-72 z-[9999]"
            style={{
              top: `${teamDropdownPosition.top}px`,
              left: `${teamDropdownPosition.left}px`,
              transform: 'translateY(-100%)',
            }}
          >
            <TeamMentionDropdown
              teams={filteredTeams}
              onSelect={onTeamMentionSelect}
              selectedIndex={selectedTeamIndex}
            />
          </div>,
          document.body,
        )}
    </form>
  );
};
